// Internationalization utility
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from '../content/config';
import type { SupportedLocale } from '../types/locale';
import type { PrinciplesContent, FoundationsContent, UIStrings, SegmentContent } from '../types/content';

class I18n {
  private currentLocale: SupportedLocale = DEFAULT_LOCALE;
  private principles: PrinciplesContent | null = null;
  private foundations: FoundationsContent | null = null;
  private ui: UIStrings | null = null;
  private enPrinciples: any = null;
  private enFoundations: any = null;
  private enUI: any = null;

  async init(locale?: SupportedLocale): Promise<void> {
    const urlLocale = this.getLocaleFromURL();
    this.currentLocale = locale || urlLocale || DEFAULT_LOCALE;

    await this.loadContent(this.currentLocale);
    this.updateDOM();
  }

  private getLocaleFromURL(): SupportedLocale | null {
    const params = new URLSearchParams(window.location.search);
    const locale = params.get('lang');

    if (locale && SUPPORTED_LOCALES.some((l) => l.code === locale)) {
      return locale as SupportedLocale;
    }
    return null;
  }

  private async loadContent(locale: SupportedLocale): Promise<void> {
    try {
      // Always load English as fallback
      const [principlesModule, foundationsModule, uiModule, enPrinciplesModule, enFoundationsModule, enUiModule] = await Promise.all([
        import(`../content/${locale}/principles.json`).catch(err => {
          console.warn(`Failed to load principles for ${locale}:`, err.message);
          return { default: {} };
        }),
        import(`../content/${locale}/foundations.json`).catch(err => {
          console.warn(`Failed to load foundations for ${locale}:`, err.message);
          return { default: {} };
        }),
        import(`../content/${locale}/ui.json`).catch(err => {
          console.warn(`Failed to load ui strings for ${locale}:`, err.message);
          return { default: {} };
        }),
        import(`../content/en/principles.json`).catch(() => ({ default: {} })),
        import(`../content/en/foundations.json`).catch(() => ({ default: {} })),
        import(`../content/en/ui.json`).catch(() => ({ default: {} })),
      ]);

      this.principles = principlesModule.default;
      this.foundations = foundationsModule.default;
      this.ui = uiModule.default;
      this.enPrinciples = enPrinciplesModule.default;
      this.enFoundations = enFoundationsModule.default;
      this.enUI = enUiModule.default;
    } catch (error) {
      // Fallback to English if loading fails
      if (locale !== DEFAULT_LOCALE) {
        console.warn(`Failed to load ${locale}, falling back to ${DEFAULT_LOCALE}`);
        await this.loadContent(DEFAULT_LOCALE);
      } else {
        console.error('Failed to load default locale content:', error);
        // Initialize with null to prevent crashes
        this.principles = null;
        this.foundations = null;
        this.ui = null;
        this.enPrinciples = null;
        this.enFoundations = null;
        this.enUI = null;
      }
    }
  }

  getString(key: string): string {
    const keys = key.split('.');
    if (keys[0] === 'ui' && keys.length > 1) {
      const value = this.ui ? (this.ui as any)[keys[1]] : undefined;
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
      // fallback to English
      const enValue = this.enUI ? (this.enUI as any)[keys[1]] : undefined;
      if (enValue !== undefined && enValue !== null && enValue !== "") {
        return enValue;
      }
      return `[Missing: ${key}]`;
    }
    return `[Missing: ${key}]`;
  }

  getSegmentContent(type: 'principles' | 'foundations', key: string): SegmentContent | null {
    let content = type === 'principles' ? this.principles : this.foundations;
    let enContent = type === 'principles' ? this.enPrinciples : this.enFoundations;
    const segment = content ? (content as any)[key] : undefined;
    const enSegment = enContent ? (enContent as any)[key] : undefined;
    
    // If no segment exists at all, return null
    if (!segment && !enSegment) {
      return null;
    }
    
    // Merge segment with English fallback for missing properties
    const merged: any = {};
    const allKeys = new Set([
      ...Object.keys(enSegment || {}),
      ...Object.keys(segment || {})
    ]);
    
    for (const key of allKeys) {
      const value = segment?.[key];
      const enValue = enSegment?.[key];
      
      // Use current locale value if it exists and is not empty, otherwise use English
      if (value !== undefined && value !== null && value !== "") {
        merged[key] = value;
      } else if (enValue !== undefined && enValue !== null && enValue !== "") {
        merged[key] = enValue;
      }
    }
    
    return Object.keys(merged).length > 0 ? merged as SegmentContent : null;
  }

  async switchLocale(newLocale: SupportedLocale): Promise<void> {
    if (newLocale === this.currentLocale) return;

    this.currentLocale = newLocale;
    await this.loadContent(newLocale);
    this.updateURL(newLocale);
    this.updateDOM();
    this.updateSVG(newLocale);

    window.dispatchEvent(
      new CustomEvent('localechange', {
        detail: { locale: newLocale },
      })
    );
  }

  private updateURL(locale: SupportedLocale): void {
    const url = new URL(window.location.href);

    if (locale === DEFAULT_LOCALE) {
      url.searchParams.delete('lang');
    } else {
      url.searchParams.set('lang', locale);
    }

    window.history.pushState({}, '', url);
  }

  private updateDOM(): void {
    // Set document direction based on locale
    const localeInfo = SUPPORTED_LOCALES.find(l => l.code === this.currentLocale);
    const dir = localeInfo?.dir || 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', this.currentLocale);

    // Update all elements with data-i18n-key attribute
    document.querySelectorAll('[data-i18n-key]').forEach((el) => {
      const key = el.getAttribute('data-i18n-key');
      if (key) {
        el.textContent = this.getString(key);
      }
    });
  }

  private updateSVG(_locale: SupportedLocale): void {
    // SVG swapping handled by Diagram component via localechange event
    // No direct DOM manipulation needed here
  }

  getCurrentLocale(): SupportedLocale {
    return this.currentLocale;
  }
}

export const i18n = new I18n();
